import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import usePlacesAutocomplete, {
  getDetails,
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';

const LIBRARIES = ['places'];
const MAP_SCRIPT_ID = 'kundli-google-places';

/**
 * Birth place picker: suggestions from Google Places; coordinates only after a list selection
 * (or “Use current location”). Free-typed text clears lat/lng until resolved.
 *
 * @param {string} props.apiKey - REACT_APP_GOOGLE_MAPS_API_KEY
 * @param {string} props.placeOfBirth - formatted place (controlled)
 * @param {number|null|undefined} props.latitude
 * @param {number|null|undefined} props.longitude
 * @param {(p: { placeOfBirth: string, latitude: number|null, longitude: number|null }) => void} props.onPlaceChange
 * @param {string} [props.error] - external validation message
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showCurrentLocation=true]
 * @param {string[]} [props.countryCodes] - e.g. ['in']; omit for worldwide
 */
export default function PlaceAutocomplete({
  apiKey,
  placeOfBirth = '',
  latitude,
  longitude,
  onPlaceChange,
  error: externalError,
  disabled = false,
  showCurrentLocation = true,
  countryCodes,
  label,
  labelHint,
  id: idProp,
}) {
  const autoId = useId();
  const inputId = idProp || `place-autocomplete-${autoId}`;
  const listboxId = `${inputId}-listbox`;
  const [internalError, setInternalError] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const suppressNotifyRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: MAP_SCRIPT_ID,
    googleMapsApiKey: apiKey || '',
    libraries: LIBRARIES,
  });

  const requestOptions = {
    types: ['(cities)'],
    ...(countryCodes?.length
      ? { componentRestrictions: { country: countryCodes.length === 1 ? countryCodes[0] : countryCodes } }
      : {}),
  };

  const {
    ready,
    value: acValue,
    suggestions: { status, data, loading: suggestionsLoading },
    setValue: setAcValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions,
    initOnMount: false,
    defaultValue: placeOfBirth || '',
  });

  useEffect(() => {
    if (isLoaded) init();
  }, [isLoaded, init]);

  // Keep input in sync when parent resets or sets value (e.g. geolocation)
  useEffect(() => {
    const next = placeOfBirth ?? '';
    if (next !== acValue) {
      suppressNotifyRef.current = true;
      setAcValue(next, false);
    }
  }, [placeOfBirth]); // eslint-disable-line react-hooks/exhaustive-deps -- sync external string only

  const notifyTyping = useCallback(
    (text) => {
      onPlaceChange({
        placeOfBirth: text,
        latitude: null,
        longitude: null,
      });
    },
    [onPlaceChange]
  );

  const handleInput = (e) => {
    const v = e.target.value;
    setAcValue(v);
    if (suppressNotifyRef.current) {
      suppressNotifyRef.current = false;
      return;
    }
    notifyTyping(v);
    setInternalError('');
  };

  const resolveFromPlaceId = useCallback(
    async (placeId, fallbackDescription) => {
      setInternalError('');
      try {
        const details = await getDetails({
          placeId,
          fields: ['formatted_address', 'geometry', 'name'],
        });
        if (!details || typeof details === 'string') {
          throw new Error('No details');
        }
        const loc = details.geometry?.location;
        if (!loc) throw new Error('No geometry');
        const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
        const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
        const formatted =
          details.formatted_address || details.name || fallbackDescription;
        suppressNotifyRef.current = true;
        setAcValue(formatted, false);
        clearSuggestions();
        onPlaceChange({
          placeOfBirth: formatted,
          latitude: lat,
          longitude: lng,
        });
      } catch {
        try {
          const results = await getGeocode({ address: fallbackDescription });
          if (!results?.length) throw new Error('geocode');
          const { lat, lng } = getLatLng(results[0]);
          suppressNotifyRef.current = true;
          setAcValue(fallbackDescription, false);
          clearSuggestions();
          onPlaceChange({
            placeOfBirth: fallbackDescription,
            latitude: lat,
            longitude: lng,
          });
        } catch {
          setInternalError('Could not resolve that place. Try another suggestion.');
          notifyTyping(fallbackDescription);
        }
      }
    },
    [setAcValue, clearSuggestions, onPlaceChange, notifyTyping]
  );

  const handleSelect = (suggestion) => {
    const desc = suggestion.description;
    setAcValue(desc, false);
    clearSuggestions();
    resolveFromPlaceId(suggestion.place_id, desc);
  };

  const handleBlur = () => {
    // allow click on suggestion before blur clears
    window.setTimeout(() => clearSuggestions(), 200);
  };

  const handleUseLocation = () => {
    if (!isLoaded || !ready || disabled || typeof navigator === 'undefined' || !navigator.geolocation) {
      setInternalError('Location not available on this device or browser.');
      return;
    }
    setGeoLoading(true);
    setInternalError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          const results = await getGeocode({ location: { lat, lng } });
          if (!results?.length) throw new Error('reverse');
          const { lat: lat2, lng: lng2 } = getLatLng(results[0]);
          const formatted = results[0].formatted_address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          suppressNotifyRef.current = true;
          setAcValue(formatted, false);
          clearSuggestions();
          onPlaceChange({
            placeOfBirth: formatted,
            latitude: lat2,
            longitude: lng2,
          });
        } catch {
          setInternalError('Could not convert your location to an address.');
        } finally {
          setGeoLoading(false);
        }
      },
      () => {
        setGeoLoading(false);
        setInternalError('Permission denied or position unavailable.');
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 }
    );
  };

  const showError = externalError || internalError;
  const mapsFailed = !!loadError;
  const waitingMaps = !apiKey || mapsFailed ? false : !isLoaded || !ready;

  return (
    <div className="w-full space-y-1.5">
      {(label || labelHint) && (
        <label htmlFor={inputId} className="block">
          {label && (
            <span className="block text-sm font-medium text-saffron-700">{label}</span>
          )}
          {labelHint && (
            <span className="block text-xs font-normal text-saffron-600/90 mt-0.5">{labelHint}</span>
          )}
        </label>
      )}

      {!apiKey && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Add <code className="text-xs">REACT_APP_GOOGLE_MAPS_API_KEY</code> to your environment to enable place search.
        </p>
      )}

      {mapsFailed && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Google Maps could not load. Check your API key and Places/Maps JavaScript API billing.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
        <div className="relative flex-1 min-w-0">
          <input
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={data.length > 0}
            aria-controls={listboxId}
            aria-autocomplete="list"
            autoComplete="off"
            disabled={disabled || !apiKey || mapsFailed || waitingMaps}
            placeholder="Start typing your birth city (e.g., Delhi, Mumbai)"
            value={acValue}
            onChange={handleInput}
            onBlur={handleBlur}
            className="w-full px-4 py-3 rounded-lg border-2 border-saffron-200 focus:border-saffron-400 focus:outline-none transition-colors text-saffron-900 placeholder-saffron-300 disabled:opacity-60"
          />
          {waitingMaps && apiKey && !mapsFailed && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-saffron-600">
              <span className="inline-block h-4 w-4 border-2 border-saffron-200 border-t-saffron-600 rounded-full animate-spin" />
              Loading…
            </div>
          )}
          {status === 'ZERO_RESULTS' && acValue.trim().length >= 2 && !suggestionsLoading && (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute z-[80] mt-1 w-full max-h-60 overflow-auto rounded-lg border border-saffron-200 bg-white shadow-lg py-1 text-sm"
            >
              <li className="px-3 py-2 text-saffron-600">No suggestions. Try a different spelling.</li>
            </ul>
          )}
          {data.length > 0 && (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute z-[80] mt-1 w-full max-h-60 overflow-auto rounded-lg border border-saffron-200 bg-white shadow-lg py-1 text-sm"
            >
              {suggestionsLoading && (
                <li className="px-3 py-2 text-saffron-600 flex items-center gap-2">
                  <span className="inline-block h-3.5 w-3.5 border-2 border-saffron-200 border-t-saffron-600 rounded-full animate-spin" />
                  Searching…
                </li>
              )}
              {data.map((s) => (
                <li key={s.place_id} role="option">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2.5 hover:bg-amber-50 active:bg-amber-100 text-saffron-900 text-[15px] leading-snug border-0 bg-transparent cursor-pointer"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(s)}
                  >
                    {s.description}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {showCurrentLocation && (
          <button
            type="button"
            onClick={handleUseLocation}
            disabled={disabled || !apiKey || mapsFailed || waitingMaps || geoLoading}
            className="shrink-0 px-3 py-2.5 rounded-lg border-2 border-saffron-300 text-saffron-800 text-sm font-semibold bg-white hover:bg-saffron-50 disabled:opacity-50 whitespace-nowrap"
          >
            {geoLoading ? 'Locating…' : 'Use current location'}
          </button>
        )}
      </div>

      {showError && (
        <p className="text-sm text-red-600 font-medium" role="alert">
          {showError}
        </p>
      )}
      {latitude != null && longitude != null && !showError && (
        <p className="text-xs text-saffron-600">
          Selected: {Number(latitude).toFixed(4)}°, {Number(longitude).toFixed(4)}°
        </p>
      )}
    </div>
  );
}
